import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import CustomEmailComponent from "../../components/CustomEmailComponent";
import ToastNotification from "../../components/ToastNotification";
import Actions from "../../components/common/Actions";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import {
  EmailTemplateType,
  TemplateBodyPlaceholder,
  TemplateSubject,
  exportEmailTemplate,
  fetchEmailTemplate,
  fetchEmailTemplateHistory,
  updateEmailTemplate,
  useEmailTemplatesQuery,
} from "../../services/EmailTemplateService";
import { IField, Params } from "../../types/table/interfaces.type";
import History from "../../components/History";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface DecodedToken {
  given_name: string;
  realm_access?: {
    roles: string[];
  };
}

const getUserRole = (): string => {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) return "";

  try {
    const decoded = jwtDecode<DecodedToken>(accessToken);
    const userRoles = decoded.realm_access?.roles || [];

    const allowedRoles = [
      "RATING_TEAM",
      "SUPER",
      "BILLING_TEAM",
      "SUPPORT_TEAM",
      "ROUTING_TEAM",
      "SALES_TEAM",
    ];

    return userRoles.find((r) => allowedRoles.includes(r)) || "";
  } catch (error) {
    console.error("Failed to decode token", error);
    return "";
  }
};

const EmailTemplatePage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<EmailTemplateType>
  >({
    search: "",
  });

  const dialogRef = useRef<EmailDialogRef>(null);

  const handleOpenDialog = (mode: "edit" | "history", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_EMAIL_TEMPLATE"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_EMAIL_TEMPLATE_HISTORY"),
    },
  });

  const fields: IField<EmailTemplateType>[] = [
    { key: "templateName", header: "Template Name" },
    { key: "templateDescription", header: "Template Description" },
    { key: "templateSubject", header: "Template Subject" },
    { key: "smtpUsername", header: "Source Email" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Email Configuration List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          mb={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportEmailTemplate(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
            permission: "GET_ALL_EMAIL_TEMPLATE_CSV",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useEmailTemplatesQuery}
          actions={actions}
          sortableFields={[
            "templateName",
            "templateDescription",
            "templateSubject",
          ]}
        />
      </Box>

      <EmailDialog
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        ref={dialogRef}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

interface EmailDialogRef {
  openDialog: (mode: "edit" | "history", id?: string) => void;
}

interface EmailDialogProps {
  onRefresh: () => void;
  appliedFilters: Params<EmailTemplateType>;
}

const EmailDialog = forwardRef<EmailDialogRef, EmailDialogProps>(
  ({ onRefresh, appliedFilters }, ref) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"edit" | "history">("edit");
    const [dialogData, setDialogData] = useState<Partial<EmailTemplateType>>(
      {}
    );
    const [selectedId, setSelectedId] = useState<string | number>("");

    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setDialogOpen(true);

        openDialog(mode, id);
        setSelectedId(id ?? "");
      },
    }));
    const TemplateBodyOptions = Object.keys(TemplateBodyPlaceholder)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        label: key,
        value: key,
      }));
    const TemplateSubjectOptions = Object.keys(TemplateSubject)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        label: key,
        value: key,
      }));

    useEffect(() => {
      const requiredFields = [
        "templateName",
        "templateDescription",
        "templateBody",
        "templateSubject",
      ];
      const areRequiredFieldsFilled = requiredFields.every(
        (field) => !!dialogData[field as keyof EmailTemplateType]
      );
      setIsSaveDisabled(!areRequiredFieldsFilled);
    }, [dialogData]);

    const openDialog = async (mode: "edit" | "history", id?: string) => {
      setDialogMode(mode);

      if (mode === "edit" && id) {
        try {
          const data = await fetchEmailTemplate(id);
          setDialogData({
            ...data,
            eventType: "RATE_CHANGE",
            smtpPort: data.smtpPort != null ? String(data.smtpPort) : "",
          });
        } catch (error: any) {
          console.error("Error fetching Email template data:", error);
        }
      } else {
        setDialogData({});
      }
    };

    const closeDialog = () => {
      setDialogOpen(false);
      setDialogData({});
    };

    const editEmailTemplate = async (
      id: string,
      data: Partial<EmailTemplateType>
    ) => {
      try {
        const payload = {
          ...data,
          id,
          smtpPort:
            data.smtpPort === "" ||
            data.smtpPort === null ||
            data.smtpPort === ""
              ? null
              : String(data.smtpPort),
        };

        await updateEmailTemplate(payload);
        onRefresh();
        setDialogOpen(false);
        ToastNotification.success("Email template updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };
    const fieldHistory: IField<EmailTemplateType>[] = [
      { key: "templateName", header: "Template Name" },
      { key: "templateDescription", header: "Template Description" },
      { key: "templateSubject", header: "Template Subject" },
      { key: "templateBody", header: "Template Body" },
      { key: "eventType", header: "Event Type" },
      { key: "createdDate", header: "Created Date" },
      { key: "smtpPassword", header: "SMTP Password" },
      { key: "historyTimestamp", header: "Date Modified" },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ];
    return (
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        mode={dialogMode}
        title={dialogMode === "edit" ? "Edit Email Template" : "History"}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={({ size = 10 }) =>
                        fetchEmailTemplateHistory(String(selectedId), {
                          ...{
                            historyTimestamp:
                              appliedFilters.historyTimestamp instanceof Date
                                ? appliedFilters.historyTimestamp.toISOString()
                                : appliedFilters.historyTimestamp,
                          },
                          size,
                        })
                      }
                    />
                  ),
                },
              ]
            : [
                {
                  type: "text",
                  label: "Email Template Name",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.templateName ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      templateName: String(value),
                    }));
                  },
                  placeholder: "Template Name",

                  disabled: true,
                },
                {
                  type: "text",
                  label: "Template Description",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  rows: 3,
                  value: dialogData.templateDescription ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      templateDescription: String(value),
                    }));
                  },
                  placeholder: "Template Description",
                },

                {
                  type: "custom",
                  label: "Template Body",
                  highlightLabelStar: true,
                  component: (
                    <CustomEmailComponent
                      textFieldLabel=""
                      textFieldValue={dialogData.templateBody ?? ""}
                      textFieldPlaceholder="Type your message here"
                      selectLabel="Template Body"
                      selectOptions={TemplateBodyOptions}
                      selectValue=""
                      onTextFieldChange={(newValue) =>
                        setDialogData((prev) => ({
                          ...prev,
                          templateBody: newValue,
                        }))
                      }
                      onSelectChange={(value) => {
                        setDialogData((prev) => ({
                          ...prev,
                          templateBody: `${prev.templateBody ?? ""} ${value}`,
                        }));
                      }}
                    />
                  ),
                },

                {
                  type: "custom",
                  label: "Template Subject",
                  highlightLabelStar: true,
                  component: (
                    <CustomEmailComponent
                      textFieldLabel=""
                      textFieldValue={dialogData.templateSubject ?? ""}
                      textFieldPlaceholder="Type your message here"
                      selectLabel="Template Subject"
                      selectOptions={TemplateSubjectOptions}
                      selectValue=""
                      onTextFieldChange={(newValue) =>
                        setDialogData((prev) => ({
                          ...prev,
                          templateSubject: newValue,
                        }))
                      }
                      onSelectChange={(value) => {
                        setDialogData((prev) => ({
                          ...prev,
                          templateSubject: `${
                            prev.templateSubject ?? ""
                          } ${value}`,
                        }));
                      }}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "From Email",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.fromEmail ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      fromEmail: String(value),
                    }));
                  },
                  placeholder: "From Email",
                },
                {
                  type: "text",
                  label: "SMTP Username",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.smtpUsername ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      smtpUsername: String(value),
                    }));
                  },
                  placeholder: "SMTP Username",
                },
                {
                  type: "text",
                  label: "SMTP Password",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.smtpPassword ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      smtpPassword: String(value),
                    }));
                  },
                  placeholder:
                    getUserRole() !== "SUPER" &&
                    dialogData.smtpUsername &&
                    (!dialogData.smtpPassword || dialogData.smtpPassword === "")
                      ? "****"
                      : "SMTP Password",
                },
                {
                  type: "text",
                  label: "SMTP Host",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.smtpHost ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      smtpHost: String(value),
                    }));
                  },
                  placeholder: "SMTP Host",
                },
                {
                  type: "text",
                  label: "SMTP Port",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.smtpPort ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      smtpPort: value === "" ? null : String(value),
                    }));
                  },
                  placeholder: "SMTP Port",
                },
                {
                  type: "text",
                  label: "Personal",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.personal ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      personal: String(value),
                    }));
                  },
                  placeholder: "Personal",
                },
                {
                  type: "switch",
                  label: "Use TLS",
                  value: dialogData.useTls ?? false,
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      useTls: Boolean(value),
                    }));
                  },
                },
                {
                  type: "switch",
                  label: "Use SSL",
                  value: dialogData.useSsl ?? false,
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      useSsl: Boolean(value),
                    }));
                  },
                },
              ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                {
                  label: "Save",
                  variant: "contained",
                  bgColor: COLORS.GOLD,
                  borderRadius: "12px",
                  onClick: async () => {
                    if (dialogMode === "edit" && dialogData.id) {
                      await editEmailTemplate(dialogData.id, dialogData);
                    }
                  },
                  disabled:
                    isSaveDisabled || !hasJwtPermission("PUT_EMAIL_TEMPLATE"),
                },
              ]
        }
      />
    );
  }
);

export default EmailTemplatePage;
